'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  totalOrders: number;
  activeOrders: number;
  totalSpent: string;
  joinDate: string;
  status: 'active' | 'inactive';
  country: string;
}

const mockCustomers: Customer[] = [
  {
    id: '1',
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+1 (555) 123-4567',
    totalOrders: 24,
    activeOrders: 3,
    totalSpent: '$12,450',
    joinDate: '2025-08-15',
    status: 'active',
    country: 'USA',
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    phone: '+1 (555) 987-6543',
    totalOrders: 18,
    activeOrders: 2,
    totalSpent: '$9,230',
    joinDate: '2025-09-22',
    status: 'active',
    country: 'USA',
  },
  {
    id: '3',
    name: 'Michael Chen',
    email: 'mchen@example.com',
    phone: '+86 138-0000-1234',
    totalOrders: 42,
    activeOrders: 5,
    totalSpent: '$28,900',
    joinDate: '2025-06-10',
    status: 'active',
    country: 'China',
  },
  {
    id: '4',
    name: 'Emily Brown',
    email: 'emily.brown@example.com',
    phone: '+44 20 7946 0958',
    totalOrders: 8,
    activeOrders: 0,
    totalSpent: '$3,120',
    joinDate: '2025-11-05',
    status: 'inactive',
    country: 'UK',
  },
];

export default function CustomerList() {
  const [customers] = useState<Customer[]>(mockCustomers);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      searchQuery === '' ||
      customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || customer.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="UsersIcon" size={24} className="text-primary" />
            <h3 className="text-sm font-medium text-muted-foreground">Total Customers</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">856</p>
        </div>
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="CheckCircleIcon" size={24} className="text-success" />
            <h3 className="text-sm font-medium text-muted-foreground">Active Customers</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">742</p>
        </div>
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="CurrencyDollarIcon" size={24} className="text-accent" />
            <h3 className="text-sm font-medium text-muted-foreground">Total Revenue</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">$1.2M</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-2">
              Search Customers
            </label>
            <div className="relative">
              <Icon
                name="MagnifyingGlassIcon"
                size={20}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or email"
                className="w-full pl-10 pr-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              <option value="all">All Customers</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Customer
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Contact
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Orders
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Total Spent
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Status
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr
                  key={customer.id}
                  className="border-t border-border hover:bg-muted/30 transition-colors"
                >
                  <td className="py-4 px-6">
                    <div>
                      <p className="text-sm font-medium text-foreground">{customer.name}</p>
                      <p className="text-xs text-muted-foreground">{customer.country}</p>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div>
                      <p className="text-sm text-foreground">{customer.email}</p>
                      <p className="text-xs text-muted-foreground">{customer.phone}</p>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {customer.totalOrders} total
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {customer.activeOrders} active
                      </p>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm font-medium text-foreground">
                      {customer.totalSpent}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        customer.status === 'active' ?'text-success bg-success/10' :'text-muted-foreground bg-muted'
                      }`}
                    >
                      {customer.status}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <button
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setShowDetailsModal(true);
                      }}
                      className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Icon name="EyeIcon" size={18} className="text-primary" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCustomers.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="UserGroupIcon" size={48} className="mx-auto mb-4" />
            <p>No customers found matching your search</p>
          </div>
        )}
      </div>

      {/* Customer Details Modal */}
      {showDetailsModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl p-6 max-w-2xl w-full shadow-xl border border-border max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-foreground">Customer Details</h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedCustomer(null);
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="XMarkIcon" size={24} className="text-muted-foreground" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Full Name</p>
                  <p className="text-base font-medium text-foreground">{selectedCustomer.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Status</p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      selectedCustomer.status === 'active' ?'text-success bg-success/10' :'text-muted-foreground bg-muted'
                    }`}
                  >
                    {selectedCustomer.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Email</p>
                  <p className="text-base font-medium text-foreground">{selectedCustomer.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Phone</p>
                  <p className="text-base font-medium text-foreground">{selectedCustomer.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Country</p>
                  <p className="text-base font-medium text-foreground">{selectedCustomer.country}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Join Date</p>
                  <p className="text-base font-medium text-foreground">{selectedCustomer.joinDate}</p>
                </div>
              </div>

              {/* Order Statistics */}
              <div className="border-t border-border pt-6">
                <h4 className="text-lg font-semibold text-foreground mb-4">Order Statistics</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-primary/5 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-primary">{selectedCustomer.totalOrders}</p>
                    <p className="text-sm text-muted-foreground mt-1">Total Orders</p>
                  </div>
                  <div className="bg-secondary/5 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-secondary">{selectedCustomer.activeOrders}</p>
                    <p className="text-sm text-muted-foreground mt-1">Active Orders</p>
                  </div>
                  <div className="bg-accent/5 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-accent">{selectedCustomer.totalSpent}</p>
                    <p className="text-sm text-muted-foreground mt-1">Total Spent</p>
                  </div>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="border-t border-border pt-6">
                <h4 className="text-lg font-semibold text-foreground mb-4">Recent Orders</h4>
                <div className="space-y-3">
                  {[
                    { tracking: 'TRK-2026-001234', status: 'In Transit', date: '2026-02-01' },
                    { tracking: 'TRK-2026-005678', status: 'Delivered', date: '2026-01-28' },
                    { tracking: 'TRK-2026-009012', status: 'Pending Pickup', date: '2026-01-25' },
                  ].map((order, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                    >
                      <div>
                        <p className="text-sm font-mono font-medium text-primary">{order.tracking}</p>
                        <p className="text-xs text-muted-foreground">{order.date}</p>
                      </div>
                      <span className="text-xs font-medium text-foreground">{order.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}